import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RouteAnalytics } from "@/components/analytics/RouteAnalytics";
import Index from "./pages/Index";
import { sectionPages } from "./pages/sections.config";
import { contentPages } from "./content/pages";

const NotFound = lazy(() => import("./pages/NotFound"));
const Disclaimers = lazy(() => import("./pages/Disclaimers"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const SectionPage = lazy(() => import("./pages/SectionPage"));
const StyleGuide = lazy(() => import("./pages/StyleGuide"));
const MediaMotionSystem = lazy(() => import("./pages/MediaMotionSystem"));
const ContentPage = lazy(() => import("./pages/ContentPage"));
const DatasyteAI = lazy(() => import("./pages/DatasyteAI"));
const AiMediQ = lazy(() => import("./pages/AiMediQ"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorks"));
const Solutions = lazy(() => import("./pages/Solutions"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const ResourceCenter = lazy(() => import("./pages/resources/ResourceCenter"));
const ResourceArticle = lazy(() => import("./pages/resources/ResourceArticle"));
const ItSecurityIntegration = lazy(() => import("./pages/solutions/ItSecurityIntegration"));
const EvidenceOverview = lazy(() => import("./pages/evidence/EvidenceOverview"));
const ClinicalApproach = lazy(() => import("./pages/evidence/ClinicalApproach"));
const ResearchPublications = lazy(() => import("./pages/evidence/ResearchPublications"));
const RegulatoryQuality = lazy(() => import("./pages/evidence/RegulatoryQuality"));
const EvidenceProgram = lazy(() => import("./pages/evidence/EvidenceProgram"));
const CompanyAbout = lazy(() => import("./pages/company/About"));
const CompanyLeadership = lazy(() => import("./pages/company/Leadership"));
const LeadershipProfile = lazy(() => import("./pages/company/LeadershipProfile"));
const CompanyRecognition = lazy(() => import("./pages/company/Recognition"));
const CompanyNews = lazy(() => import("./pages/company/News"));
const RequestDemo = lazy(() => import("./pages/RequestDemo"));
const ExperienceDemo = lazy(() => import("./pages/ExperienceDemo"));
const ResponsibleAI = lazy(() => import("./pages/ResponsibleAI"));
const ReadinessIndex = lazy(() => import("./pages/ReadinessIndex"));
const ValueModelPage = lazy(() => import("./pages/ValueModel"));
const EnterpriseDeployment = lazy(() => import("./pages/EnterpriseDeployment"));
const WhyPathFree = lazy(() => import("./pages/WhyPathFree"));
const PathFreeTechnologies = lazy(() => import("./pages/PathFreeTechnologies"));

const Glossary = lazy(() => import("./pages/Glossary"));
const Blog = lazy(() => import("./pages/Blog"));
const InstituteHome = lazy(() => import("./pages/institute/InstituteHome"));
const StateOfIntelligentResuscitation = lazy(() => import("./pages/institute/StateOfIntelligentResuscitation"));
const Newsroom = lazy(() => import("./pages/newsroom/Newsroom"));
const PressKit = lazy(() => import("./pages/newsroom/PressKit"));
const TrustCenter = lazy(() => import("./pages/TrustCenter"));
const StrategicPartnerships = lazy(() => import("./pages/StrategicPartnerships"));

const App = () => (
  <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouteAnalytics />
        <Suspense fallback={null}>

          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/request-demo" element={<RequestDemo />} />
            <Route path="/experience" element={<ExperienceDemo />} />
            <Route path="/demo" element={<Navigate to="/request-demo" replace />} />
            <Route path="/disclaimers" element={<Disclaimers />} />

            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            {/* Internal design-system reference — intentionally not in public nav */}
            <Route path="/style-guide" element={<StyleGuide />} />
            <Route path="/media-motion-system" element={<MediaMotionSystem />} />


            <Route path="/datasyteai" element={<DatasyteAI />} />
            <Route path="/aimediq" element={<AiMediQ />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/solutions/it-security-integration" element={<ItSecurityIntegration />} />
            <Route path="/solutions/:slug" element={<SolutionPage />} />

            <Route path="/resources" element={<ResourceCenter />} />
            <Route path="/resources/library/:slug" element={<ResourceArticle />} />

            <Route path="/responsible-ai" element={<ResponsibleAI />} />
            <Route path="/readiness-index" element={<ReadinessIndex />} />
            <Route path="/value-model" element={<ValueModelPage />} />
            <Route path="/enterprise-deployment" element={<EnterpriseDeployment />} />
            <Route path="/why-pathfree" element={<WhyPathFree />} />
            <Route path="/glossary" element={<Glossary />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/institute" element={<InstituteHome />} />
            <Route
              path="/institute/state-of-intelligent-resuscitation"
              element={<StateOfIntelligentResuscitation />}
            />
            <Route path="/newsroom" element={<Newsroom />} />
            <Route path="/newsroom/press-kit" element={<PressKit />} />
            <Route path="/trust-center" element={<TrustCenter />} />
            <Route path="/partnerships" element={<StrategicPartnerships />} />
            <Route path="/evidence" element={<EvidenceOverview />} />
            <Route path="/evidence/clinical-approach" element={<ClinicalApproach />} />
            <Route path="/evidence/research-publications" element={<ResearchPublications />} />
            <Route path="/evidence/regulatory-quality" element={<RegulatoryQuality />} />
            <Route path="/evidence/program" element={<EvidenceProgram />} />
            {/* Legacy evidence paths */}
            <Route path="/evidence/research-insights" element={<Navigate to="/evidence/research-publications" replace />} />
            <Route path="/regulatory-status" element={<Navigate to="/evidence/regulatory-quality" replace />} />

            <Route path="/company" element={<Navigate to="/company/about" replace />} />
            {/* Brand entity page. Every company-name alias redirects here so
                ranking signals stay on one canonical URL. */}
            <Route path="/pathfree-technologies" element={<PathFreeTechnologies />} />
            <Route path="/pathfree" element={<Navigate to="/pathfree-technologies" replace />} />
            <Route
              path="/pathfree-technologies-corporation"
              element={<Navigate to="/pathfree-technologies" replace />}
            />
            <Route path="/pathfree-technologies-corp" element={<Navigate to="/pathfree-technologies" replace />} />

            <Route path="/company/about" element={<CompanyAbout />} />
            <Route path="/company/leadership" element={<CompanyLeadership />} />
            <Route path="/company/leadership/:slug" element={<LeadershipProfile />} />
            <Route path="/company/partnerships" element={<CompanyRecognition />} />
            <Route path="/company/recognition" element={<Navigate to="/company/partnerships" replace />} />
            <Route path="/company/news" element={<CompanyNews />} />

            {contentPages.map((page) => (
              <Route key={page.slug} path={`/${page.slug}`} element={<ContentPage />} />
            ))}

            {sectionPages.map((page) => (
              <Route key={page.slug} path={`/${page.slug}`} element={<SectionPage />} />
            ))}
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
);


export default App;
